<template>
  <Modal
    v-model="isVisible"
    :hide-header="true"
    :dialog-class="'modal-dialog-centered'"
    :backdrop="'static'"
    :aria-label="$t('onboarding.geoMismatchModal.ariaLabel')"
  >
    <!-- The dialog role, aria-modal and the Tab trap all live on Modal.vue's
         own dialog box now. Repeating them here nested a second aria-modal
         dialog inside the first, which assistive tech reads as two competing
         modal boundaries. Escape stays local: this modal is backdrop="static",
         so Modal deliberately does not close on Escape, but dismissing here is
         allowed. -->
    <div ref="modalRoot" class="gmm" @keydown="handleKeyDown">
      <button
        ref="closeButton"
        type="button"
        class="gmm-close"
        :aria-label="$t('common.close')"
        @click="onDismiss"
      >
        ×
      </button>

      <div class="gmm-header">
        <div class="gmm-question" aria-hidden="true">?</div>
        <img
          v-if="geoFlag"
          :src="geoFlag"
          :alt="$t('onboarding.geoMismatchModal.geoFlagAlt')"
          class="gmm-geo-flag"
        />
      </div>

      <div class="gmm-buttons">
        <button
          ref="suggestedButton"
          type="button"
          class="gmm-button gmm-button--suggested"
          :aria-label="$t('onboarding.geoMismatchModal.switchTo', { language: suggestedTranslated })"
          @click="onChangeToSuggested"
        >
          <img
            v-if="suggestedFlag"
            :src="suggestedFlag"
            :alt="suggestedLang?.altText"
            class="gmm-button-flag"
          />
          <span
            class="gmm-button-label"
            :lang="suggestedLang?.nativeScriptLang"
          >{{ suggestedLang?.nativeScriptName }}</span>
        </button>

        <button
          ref="keepButton"
          type="button"
          class="gmm-button gmm-button--keep"
          :aria-label="$t('onboarding.geoMismatchModal.keep', { language: pickedTranslated })"
          @click="onKeepPick"
        >
          <img
            v-if="pickedFlag"
            :src="pickedFlag"
            :alt="pickedLang?.altText"
            class="gmm-button-flag"
          />
          <span
            class="gmm-button-label"
            :lang="pickedLang?.nativeScriptLang"
          >{{ pickedLang?.nativeScriptName }}</span>
        </button>
      </div>
    </div>
  </Modal>
</template>

<script>
import Modal from "./Modal.vue";
import { Languages } from "@/data/Language.js";
import { flagsStore } from "@/store/countryFlags.js";

export default {
  name: "GeoMismatchModal",
  components: { Modal },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    geoCountry: {
      type: String,
      default: null,
    },
    geoFlagCode: {
      type: String,
      default: null,
    },
    pickedCode: {
      type: String,
      required: true,
    },
    suggestedCode: {
      type: String,
      required: true,
    },
  },
  emits: ["update:modelValue", "keep-pick", "change-to-suggested", "dismiss"],
  computed: {
    isVisible: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    pickedLang() {
      return Languages[this.pickedCode] || null;
    },
    suggestedLang() {
      return Languages[this.suggestedCode] || null;
    },
    pickedFlag() {
      return flagsStore.getFlag(this.pickedCode);
    },
    suggestedFlag() {
      return flagsStore.getFlag(this.suggestedCode);
    },
    geoFlag() {
      if (!this.geoFlagCode) return null;
      return flagsStore.getFlag(this.geoFlagCode);
    },
    pickedTranslated() {
      return this.$t(`languages.${this.pickedCode}`);
    },
    suggestedTranslated() {
      return this.$t(`languages.${this.suggestedCode}`);
    },
  },
  watch: {
    modelValue: {
      /* immediate, because OnboardingWizard sets pendingMismatchPick (the v-if)
         and showGeoMismatchModal (the v-model) in the same tick: the component
         mounts already open, so a plain watcher never runs and focus is left
         outside .gmm — where the Escape handler below cannot see it, and this
         dialog is backdrop="static" so Modal deliberately will not close on
         Escape either. */
      immediate: true,
      handler(open) {
        if (open) {
          this.$nextTick(() => {
            this.$refs.suggestedButton?.focus();
          });
        }
      },
    },
  },
  methods: {
    onChangeToSuggested() {
      this.$emit("change-to-suggested");
      this.isVisible = false;
    },
    onKeepPick() {
      this.$emit("keep-pick");
      this.isVisible = false;
    },
    onDismiss() {
      this.$emit("dismiss");
      this.isVisible = false;
    },
    /* Escape only. The Tab trap this used to duplicate now lives in Modal.vue
       and enumerates the same three buttons dynamically, so running both meant
       two handlers fighting over the same keystroke. */
    handleKeyDown(event) {
      if (event.key === "Escape") {
        event.preventDefault();
        this.onDismiss();
      }
    },
  },
};
</script>

<style scoped>
.gmm {
  position: relative;
  padding: 0.5rem 0.25rem 0.25rem;
}

.gmm-close {
  position: absolute;
  top: -0.25rem;
  right: -0.25rem;
  background: none;
  border: none;
  font-size: 1.75rem;
  line-height: 1;
  color: var(--al-text-muted);
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
}

.gmm-close:hover,
.gmm-close:focus-visible {
  color: var(--al-text);
  background-color: var(--al-surface-sunken);
  outline: none;
}

.gmm-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin: 0.5rem 0 1.5rem;
}

/* AA: --al-amber text on white/soft backgrounds is ~2.15:1 (fails), so the
   glyph uses gray-700-level darkness instead of amber. */
.gmm-question {
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background-color: color-mix(in srgb, var(--al-amber) 15%, var(--al-surface-raised));
  color: var(--al-text);
  font-size: 1.75rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gmm-geo-flag {
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid var(--al-border);
}

.gmm-buttons {
  display: flex;
  gap: 0.75rem;
  flex-direction: row;
}

@media (max-width: 500px) {
  .gmm-buttons {
    flex-direction: column;
  }
}

.gmm-button {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem 0.75rem;
  border-radius: var(--al-radius);
  border: 2px solid var(--al-border);
  background-color: var(--al-surface-sunken);
  cursor: pointer;
  transition: transform var(--al-dur-short) var(--al-ease-move),
    border-color var(--al-dur-short) var(--al-ease-move),
    box-shadow var(--al-dur-short) var(--al-ease-move);
  min-height: 56px;
}

.gmm-button:hover {
  transform: translateY(-2px);
  border-color: var(--al-primary);
  box-shadow: 0 4px 6px color-mix(in srgb, var(--al-primary) 15%, transparent);
}

.gmm-button:focus-visible {
  outline: 3px solid var(--al-primary);
  outline-offset: 2px;
}

/* Selected-chip recipe: primary border + primary-soft bg + ink/gray-700 text. */
.gmm-button--suggested {
  background-color: var(--al-primary-soft);
  border-color: var(--al-primary);
}

.gmm-button-flag {
  width: 2.5rem;
  height: auto;
  border-radius: 0.25rem;
  border: 1px solid var(--al-border);
}

.gmm-button-label {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--al-text);
  text-align: center;
}
</style>
